"use client";

import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import type { CourseMetadataItem } from "@/lib/admin/course-details";

export default function MetadataTab({
  domains,
  goals,
  departments,
  editingId,
  editingLabel,
  setEditingId,
  setEditingLabel,
  metadataBusyKey,
  newDomainLabel,
  setNewDomainLabel,
  newGoalLabel,
  setNewGoalLabel,
  newDepartmentLabel,
  setNewDepartmentLabel,
  handleDeleteMetadata,
  handleSaveMetadataLabel,
  handleAddMetadata,
  requireUserFutureGoal,
  onChangeRequireUserFutureGoal,
  preferencePromptSaving,
}: {
  domains: CourseMetadataItem[];
  goals: CourseMetadataItem[];
  departments: CourseMetadataItem[];
  editingId: number | null;
  editingLabel: string;
  setEditingId: (id: number | null) => void;
  setEditingLabel: (label: string) => void;
  metadataBusyKey: string | null;
  newDomainLabel: string;
  setNewDomainLabel: (value: string) => void;
  newGoalLabel: string;
  setNewGoalLabel: (value: string) => void;
  newDepartmentLabel: string;
  setNewDepartmentLabel: (value: string) => void;
  handleDeleteMetadata: (id: number) => void;
  handleSaveMetadataLabel: (id: number) => void;
  handleAddMetadata: (type: "DOMAIN" | "FUTURE_GOAL" | "DEPARTMENT") => void;
  requireUserFutureGoal: boolean;
  onChangeRequireUserFutureGoal: (next: boolean) => void;
  preferencePromptSaving?: boolean;
}) {
  const renderList = (items: CourseMetadataItem[], title: string, type: "DOMAIN" | "FUTURE_GOAL" | "DEPARTMENT") => (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{items.length}</span>
      </div>
      <div className="space-y-2 p-4">
        {items.length === 0 && <p className="text-sm italic text-slate-500">No {type === "DOMAIN" ? "domains" : type === "FUTURE_GOAL" ? "goals" : "departments"} added yet.</p>}
        {items.map((item) => (
          <div key={item.id} className="rounded-lg border border-slate-200 px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-800">{item.label}</p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(item.id);
                    setEditingLabel(item.label);
                  }}
                  className="inline-flex items-center rounded-md border border-slate-200 bg-white p-1.5 text-slate-700 hover:bg-slate-50"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteMetadata(item.id)}
                  disabled={metadataBusyKey === `delete:${item.id}`}
                  className="inline-flex items-center rounded-md border border-red-200 bg-red-50 p-1.5 text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {editingId === item.id && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={() => handleSaveMetadataLabel(item.id)}
                  disabled={!editingLabel.trim()}
                  className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 p-1.5 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2">
          <input
            value={type === "DOMAIN" ? newDomainLabel : type === "FUTURE_GOAL" ? newGoalLabel : newDepartmentLabel}
            onChange={(e) => {
              if (type === "DOMAIN") setNewDomainLabel(e.target.value);
              else if (type === "FUTURE_GOAL") setNewGoalLabel(e.target.value);
              else setNewDepartmentLabel(e.target.value);
            }}
            placeholder={`Add ${type === "DOMAIN" ? "domain" : type === "FUTURE_GOAL" ? "future goal" : "department"}`}
            className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <button
            type="button"
            onClick={() => handleAddMetadata(type)}
            disabled={metadataBusyKey === `add:${type}` || !(type === "DOMAIN" ? newDomainLabel : type === "FUTURE_GOAL" ? newGoalLabel : newDepartmentLabel).trim()}
            className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            checked={requireUserFutureGoal}
            disabled={preferencePromptSaving}
            onChange={(e) => {
              void onChangeRequireUserFutureGoal(e.target.checked);
            }}
          />
          <span>
            <span className="text-sm font-semibold text-slate-900">Require future goal from students</span>
            <span className="mt-0.5 block text-sm text-slate-600">
              When checked, learners must pick both an area of interest and a future goal. When unchecked, only the
              area of interest is required (future goals stay optional for personalization when listed below).
            </span>
          </span>
        </label>
        {preferencePromptSaving && (
          <p className="mt-2 text-xs text-slate-500">Saving preference…</p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {renderList(domains, "Domains", "DOMAIN")}
        {renderList(goals, "Future goals", "FUTURE_GOAL")}
        {renderList(departments, "Departments", "DEPARTMENT")}
      </div>
    </div>
  );
}
